import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import { paginationDefaults } from "../../config/settingsDefaults";
import {
  DynamicRequestFilter,
  DynamicRequestSort,
} from "../../types/dynamicQueryTypes";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import {
  IssueTrackerCategoryType,
  ISSUE_TRACKER_CATEGORIES,
} from "../../types/qualityRecordTypes";
import { IdAsUUID } from "../../types/common/commonTypes";
import PageHeaderComponent from "../../components/common/PageHeader";
import stringUtils from "../../utils/stringUtils";

interface IssueData {
  id: IdAsUUID;
  emp_first: string;
  emp_last: string;
  repby_first: string;
  repby_last: string;
  count: number;
  occurrence: Date;
  category: IssueTrackerCategoryType;
  other: string | null;
  description: string;
  created_at: Date;
  updated_at: Date;
}

const FilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
  placeholder: string;
}> = ({ finalSet, placeholder }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder={placeholder}
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

export const PageIssueRecordList: React.FC = () => {
  const navigate = useNavigate();

  const { user } = useAuth();

  const [error, setError] = useState<string | null>(null);

  const [targetReportedBy, setTargetReportedBy] = useState<string>("");
  const [targetEmp, setTargetEmp] = useState<string>("");
  const [targetCategory, setTargetCategory] =
    useState<IssueTrackerCategoryType | null>(null);

  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });

  const [orderBy, setOrderBy] = useState<string>("employee");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const sorts: DynamicRequestSort[] = useMemo(() => {
    switch (orderBy) {
      case "employee":
        return [
          {
            table: "employees",
            alt_name: "emp",
            field: "first_name",
            asc: order === "asc",
          },
          {
            table: "employees",
            alt_name: "emp",
            field: "last_name",
            asc: order === "asc",
          },
          {
            table: "issue_tracker",
            alt_name: "iss",
            field: "issue_occurrence",
            asc: true,
          },
        ];
      case "repby":
        return [
          {
            table: "employees",
            alt_name: "repby",
            field: "first_name",
            asc: order === "asc",
          },
          {
            table: "employees",
            alt_name: "repby",
            field: "last_name",
            asc: order === "asc",
          },
          {
            table: "issue_tracker",
            alt_name: "iss",
            field: "issue_occurrence",
            asc: true,
          },
        ];
      case "category":
        return [
          {
            table: "issue_tracker",
            alt_name: "iss",
            field: "issue_category",
            asc: order === "asc",
          },
          {
            table: "issue_tracker",
            alt_name: "iss",
            field: "issue_occurrence",
            asc: true,
          },
        ];
      case "occurrence":
        return [
          {
            table: "issue_tracker",
            alt_name: "iss",
            field: "issue_occurrence",
            asc: order === "asc",
          },
        ];
      default:
        return [];
    }
  }, [order, orderBy]);

  const filters: DynamicRequestFilter[][] = useMemo(() => {
    const filts: DynamicRequestFilter[][] = [];
    if (targetEmp !== "") {
      filts[0] = [];
      filts[1] = [];
      filts[0].push({
        table: "employees",
        alt_name: "emp",
        field: "name",
        operator: "contains",
        value: targetEmp,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      filts[1].push({
        table: "employees",
        alt_name: "emp",
        field: "code",
        operator: "contains",
        value: targetEmp,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetReportedBy !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "employees",
        alt_name: "repby",
        field: "first_name",
        operator: "contains",
        value: targetReportedBy,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] === undefined) {
        filts[1] = [];
      }
      filts[1].push({
        table: "employees",
        alt_name: "repby",
        field: "last_name",
        operator: "contains",
        value: targetReportedBy,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (!!targetCategory) {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "issue_tracker",
        alt_name: "iss",
        field: "issue_category",
        operator: "contains",
        value: targetCategory,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] === undefined) {
        filts[1] = [];
      }
      filts[1].push({
        table: "issue_tracker",
        alt_name: "iss",
        field: "issue_category",
        operator: "contains",
        value: targetCategory,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    return filts;
  }, [targetReportedBy, targetEmp, targetCategory]);

  const {
    data: issueResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQDynamicList({
    page: pagination.page,
    page_size: pagination.size,
    primary: "issue_tracker",
    alt_name: "iss",
    primary_fields: [
      "id",
      "created_at",
      "updated_at",
      "issue_category",
      "issue_other",
      "issue_description",
      "issue_occurrence",
      "issue_count",
    ],
    joins: [
      {
        table: "employees",
        alt_name: "emp",
        fields: ["id", "first_name", "last_name"],
        condition: {
          remote_table: "issue_tracker",
          alt_name: "iss",
          remote_field: "employee_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "employees",
        alt_name: "repby",
        fields: ["id", "first_name", "last_name"],
        condition: {
          remote_table: "issue_tracker",
          alt_name: "iss",
          remote_field: "reported_by_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: filters,
    sorts: sorts,
    subqueries: [],
  });

  const [issueRecordObjs, recCount]: [IssueData[], number] = useMemo(() => {
    const issueRecordObjs: IssueData[] = [];
    if (issueResponse?.items) {
      issueResponse.items.forEach((val) => {
        console.log("CF Value", val);
        issueRecordObjs.push({
          emp_first: val["emp.first_name"],
          emp_last: val["emp.last_name"],
          repby_first: val["repby.first_name"],
          repby_last: val["repby.last_name"],
          occurrence: new Date(val["iss.issue_occurrence"]),
          count: parseInt(val["iss.issue_count"]),
          category: val["iss.issue_category"],
          other: val["iss.issue_other"],
          description: val["iss.issue_description"],
          id: val["iss.id"],
          created_at: new Date(val["iss.created_at"]),
          updated_at: new Date(val["iss.updated_at"]),
        });
      });
    }

    return [issueRecordObjs, issueResponse?.total || 0];
  }, [issueResponse]);

  const [displayProcessDialog, setDisplayProcessDialog] =
    useState<boolean>(false);
  const [processDialogError, setProcessDialogError] = useState<string | null>(
    null,
  );

  const handleProcessDialogClose = (
    event: any,
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (reason === "backdropClick") {
      return;
    }
    setDisplayProcessDialog(false);
    setProcessDialogError(null);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  //   const approveComplaint = async (id: string) => {
  //     if (!id) {
  //       setError("Missing ID, Cannot Approve Complaint");
  //       return;
  //     }
  //     setDisplayProcessDialog(true);
  //     try {
  //       const appResult = await approveComplaintMutation.mutateAsync({ id });
  //       if (appResult.success) {
  //         const procResult = await postProcComplaintMutation.mutateAsync({ id });
  //         if (!procResult.success) {
  //           setProcessDialogError(
  //             `Complaint Post Processing failed: ${procResult.message || "UNKNOWN REASON"}`,
  //           );
  //         } else {
  //           setTimeout(() => {
  //             handleProcessDialogClose(null, "escapeKeyDown");
  //           }, 5000);
  //         }
  //       } else {
  //         setProcessDialogError(
  //           `Complaint Approval failed: ${appResult.message || "UNKNOWN REASON"}`,
  //         );
  //       }
  //     } catch (e) {
  //       setError(
  //         "There was an error completing the Complaint Approval Process. Notify IT so it can be fixed. If you want to be extra helpful, you can send a screenshot of the browser console.",
  //       );
  //       console.error(e);
  //       return;
  //     }
  //   };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <PageHeaderComponent
          headerTextContent={{ text: "Issue Records" }}
          user={user}
          otherButtons={[
            {
              id: "create-issue",
              text: "Create New",
              variant: "contained",
              onClick: () => {
                setError("Not Set Up Yet, Sorry! :(");
              },
              startIcon: <AddIcon />,
            },
          ]}
          debugPrintValues={{ records: issueRecordObjs }}
        />
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" flexWrap={"wrap"} gap={1}>
          <FilterBox
            finalSet={setTargetEmp}
            placeholder="Filter By Employee (First or Last Name)"
          />
          <FilterBox
            finalSet={setTargetReportedBy}
            placeholder="Filter By Reported By (First or Last Name)"
          />
          <Autocomplete
            value={targetCategory}
            options={ISSUE_TRACKER_CATEGORIES}
            onChange={(e, v) => setTargetCategory(v)}
            fullWidth
            renderInput={(params) => (
              <TextField {...params} placeholder="Filter By Issue Category" />
            )}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <TablePagination
          component="div"
          count={recCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={orderBy === "employee" ? order : false}
                width="15%"
              >
                <TableSortLabel
                  active={orderBy === "employee"}
                  direction={orderBy === "employee" ? order : "asc"}
                  onClick={() => handleSort("employee")}
                >
                  Employee
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "category" ? order : false}
                width="30%"
              >
                <TableSortLabel
                  active={orderBy === "category"}
                  direction={orderBy === "category" ? order : "asc"}
                  onClick={() => handleSort("category")}
                >
                  Issue Details
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "occurrence" ? order : false}
                width="15%"
              >
                <TableSortLabel
                  active={orderBy === "occurrence"}
                  direction={orderBy === "occurrence" ? order : "asc"}
                  onClick={() => handleSort("occurrence")}
                >
                  Date of Occurrence
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "repby" ? order : false}
                width="15%"
              >
                <TableSortLabel
                  active={orderBy === "repby"}
                  direction={orderBy === "repby" ? order : "asc"}
                  onClick={() => handleSort("repby")}
                >
                  Reported By
                </TableSortLabel>
              </TableCell>
              <TableCell width="25%">Additional Information</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : issueRecordObjs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {filters.length > 0
                      ? "No Issue Records found matching your search."
                      : "No Issue Records found."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              issueRecordObjs.map((iss) => (
                <TableRow key={iss.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {`${iss.emp_first} ${iss.emp_last}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.5}>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Category:{" "}
                        {stringUtils.capFirstLetter(
                          iss.category.replaceAll("_", " "),
                        )}
                        {!!iss.other ? ` (${iss.other})` : ""}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        {iss.description}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {iss.occurrence.toLocaleDateString("en-US")}
                    </Typography>
                  </TableCell>
                  <TableCell>{`${iss.repby_first} ${iss.repby_last}`}</TableCell>
                  <TableCell>UNDER CONSTRUCTION</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={recCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
      {/* <Dialog
        open={displayProcessDialog}
        onClose={(e, r) => {
          handleProcessDialogClose(e, r);
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Stack
            direction="column"
            width="100%"
            justifyContent={"center"}
            gap={3}
          >
            {!!processDialogError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {processDialogError}
              </Alert>
            )}
            <Box
              display="flex"
              gap={2}
              justifyContent="space-evenly"
              width="50%"
            >
              <Typography variant="h5">Approval</Typography>
              {approveComplaintMutation.isPending ? (
                <CircularProgress />
              ) : approveComplaintMutation.data?.success ? (
                <CheckIcon color="primary" />
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
            <Box
              display="flex"
              gap={2}
              justifyContent="space-evenly"
              width="50%"
            >
              <Typography variant="h5">Post Processing</Typography>
              {approveComplaintMutation.isPending ||
              postProcComplaintMutation.isPending ? (
                <CircularProgress />
              ) : postProcComplaintMutation.data?.success ? (
                <CheckIcon color="primary" />
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
            {approveComplaintMutation.isPending ||
            postProcComplaintMutation.isPending ? (
              <Typography variant="h5">
                Page will return to Complaint List View upon request
                completion...
              </Typography>
            ) : !(
                !!approveComplaintMutation.data?.success &&
                !!postProcComplaintMutation.data?.success
              ) ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  handleProcessDialogClose(null, "escapeKeyDown");
                }}
              >
                Close
              </Button>
            ) : (
              <Typography variant="h5">
                Requests completed, returning to Complaint List View mode...
              </Typography>
            )}
          </Stack>
        </DialogContent>
      </Dialog> */}
    </Box>
  );
};
